import React, { useState } from "react";
import { Post } from "@/core/types/postTypes";
import { MediaCardContainer, MediaDisplay, AspectRatio } from "@/components/MediaCard";

interface CaptionInputPanelProps {
  post: Post;
  onGenerate: (description: string, keywords: string[]) => void;
  isLoading: boolean;
  initialDescription?: string;
  initialKeywords?: string[];
  clientId: string;
}

export const CaptionInputPanel: React.FC<CaptionInputPanelProps> = ({
  post,
  onGenerate,
  isLoading,
  initialDescription = "",
  initialKeywords = [],
  clientId,
}) => {
  const [description, setDescription] = useState(initialDescription);
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>(initialKeywords);

  // Extract AI-generated label descriptions from images
  const imageLabels = React.useMemo(() => {
    if (!post.mediaItems) return [];

    return post.mediaItems
      .map(media => media.frontendData?.aiAnalysis?.labelDescription)
      .filter((label): label is string => Boolean(label));
  }, [post.mediaItems]);

  const handleAddKeyword = () => {
    if (keywordInput.trim() && keywords.length < 20) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    let finalDescription = description.trim();

    // Append AI-generated image context if available and not already included
    if (imageLabels.length > 0) {
      const labelsText = imageLabels.join(' ');
      if (!finalDescription.includes(labelsText)) {
        finalDescription = finalDescription
          ? `${finalDescription}\n\nImage context: ${labelsText}`
          : `Image context: ${labelsText}`;
      }
    }

    if (finalDescription) {
      onGenerate(finalDescription, keywords);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddKeyword();
    }
  };

  return (
    <div className="caption-input-panel space-y-4">
      {post.mediaItems && post.mediaItems.length > 0 && (
        <div className="post-images-preview flex gap-4 pb-4">
          {post.mediaItems.slice(0, 3).map((media) => {
            // Different sizes and aspect ratios based on post type
            const isReel = post.postType === "reel";
            const widthClass = isReel ? "w-36" : "w-48"; // 144px for reels, 192px for posts
            const aspectRatio: AspectRatio = isReel ? "9:16" : "4:5";

            return (
              <div
                key={media.id}
                className={`${widthClass} shrink-0 rounded-lg border border-gray-200 shadow-xs overflow-hidden`}
              >
                <MediaCardContainer aspectRatio={aspectRatio}>
                  <MediaDisplay mediaItem={media} useThumbnail={true} showPlayButton={media.isVideo()} />
                </MediaCardContainer>
              </div>
            );
          })}
          {post.mediaItems.length > 3 && (
            <div
              className={`${
                post.postType === "reel" ? "w-36 aspect-9/16" : "w-48 aspect-4/5"
              } shrink-0 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center`}
            >
              <span className="text-gray-600 text-sm font-medium">
                +{post.mediaItems.length - 3} more
              </span>
            </div>
          )}
        </div>
      )}

      {imageLabels.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 mb-1">AI Image Analysis Available</p>
              <p className="text-xs text-blue-800">{imageLabels.join(' ')}</p>
              <p className="text-xs text-blue-600 mt-1 italic">This context will be automatically included in caption generation</p>
            </div>
          </div>
        </div>
      )}

      <div className="form-group">
        <label
          htmlFor="image-description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Image Description {imageLabels.length === 0 && '*'}
          <span className="block text-xs text-gray-500 font-normal">
            {imageLabels.length > 0
              ? 'Add additional context or leave empty to use AI analysis'
              : 'Describe what\'s in the image for AI context'}
          </span>
        </label>
        <textarea
          id="image-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={imageLabels.length > 0
            ? "Optional: Add additional context or instructions..."
            : "E.g., A beautiful sunset over the mountains with vibrant orange and purple colors..."}
          rows={4}
          maxLength={5000}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
        />
        <div className="text-xs text-gray-500 mt-1 text-right">
          {description.length}/5000
        </div>
      </div>

      <div className="form-group">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Keywords
          <span className="block text-xs text-gray-500 font-normal">
            SEO keywords for this post (max 20)
          </span>
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Add keyword..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <button
            onClick={handleAddKeyword}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Add
          </button>
        </div>
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {keywords.map((keyword, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-sm text-sm"
              >
                {keyword}
                <button
                  onClick={() => handleRemoveKeyword(index)}
                  className="text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={handleSubmit}
        disabled={(imageLabels.length === 0 && !description.trim()) || isLoading}
        className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? "Generating..." : "Generate Captions"}
      </button>
    </div>
  );
};
